import type { JSX } from 'preact';
import { chapterVideo } from '../video/chapter-videos.js';

type Props = {
  book: string;
  chapter: number;
  languageIso: string;
};

function PlayIcon(props: JSX.SVGAttributes<SVGSVGElement>) {
  return (
    <svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {/* filled play triangle, slightly offset right for optical balance */}
      <path d="M8 5.5l12 6.5-12 6.5V5.5z" fill="currentColor" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg
      width={11}
      height={11}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.5"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

export function WatchLink({ book, chapter, languageIso }: Props) {
  const video = chapterVideo(book, chapter, languageIso);
  if (!video) return null;

  return (
    <a
      class="watch-card"
      href={video.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Watch ${video.title} on YouTube — opens in YouTube`}
      data-video-source={video.source}
    >
      {/* ── cinematic preview area ── */}
      <div class="watch-card-preview" aria-hidden="true">
        {/* Real thumbnail when available — gradient shows through as fallback */}
        {video.thumbnailUrl ? (
          <img
            class="watch-card-thumb"
            src={video.thumbnailUrl}
            alt=""
            loading="lazy"
            decoding="async"
            width={320}
            height={180}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : null}
        {/* film-strip perforations via ::before / ::after in CSS */}
        <div class="watch-card-play">
          <PlayIcon />
        </div>
      </div>

      {/* ── title + external badge ── */}
      <div class="watch-card-meta">
        <span class="watch-card-title">{video.title}</span>
        <span class="watch-card-ext">
          YouTube <ExternalIcon />
        </span>
      </div>
    </a>
  );
}
